import { styled as muiStyled, Button, IconButton } from '@mui/material'

const PURPLE_THEME = {
  primary: '#7450aa',
  light: '#8b6eb8',
  dark: '#47306a',
  lightBg: '#c2b2da',
  contrastText: '#fff',
}

export const SplitEditorContainer = muiStyled('div')(
  () => `
        display: flex;
        gap: 8px;
        padding-left: 8px;
        flex: 1;
        min-height: 0;
        align-items: stretch;
        @media (max-width: 1100px) {
          flex-direction: column;
          padding-right: 8px;
        }
        @media print {
          height: auto;
          padding-left: 0px;
          display: block;
          overflow: visible !important;
          /* Hide scrollbars */
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
          &::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          /* Remove shadows */
          box-shadow: none !important;
        }
`,
)

export const ActuallyPrintableContainer = muiStyled('div')(
  () => `
  /* In-flow so print captures it; hidden on screen via height/overflow */
  position: relative;
  width: 794px;
  max-width: 100%;
  height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
  @media print {
    position: static;
    width: 100%;
    height: auto;
    visibility: visible;
    pointer-events: auto;
    overflow: visible !important;
    /* Hide scrollbars */
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
    &::-webkit-scrollbar {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }
    /* Remove all shadows */
    box-shadow: none !important;
    * {
      box-shadow: none !important;
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;
      &::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
      }
    }
  }
`,
)

export const FormControlsContainer = muiStyled('div')(
  ({ theme }) => `
  background: ${theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]};
  border-radius: 12px;
  padding: 16px;
  /* The left controls panel scrolls on its OWN scrollbar, fully independent of the
     preview. It fills the bounded split height; min-height:0 is REQUIRED so this flex
     child can shrink below its content and actually show its own scrollbar. */
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  min-width: 400px;
  max-width: 560px;
  flex: 0 0 auto;
  width: clamp(400px, 42vw, 560px);
  border: 1px solid ${theme.palette.divider};

  @media (max-width: 1100px) {
    /* Stacked layout: behave normally so the whole page scrolls. */
    height: auto;
    min-height: 0;
    overflow: visible;
    min-width: 0;
    max-width: none;
    width: 100%;
  }

  @media print {
    display: none;
  }
`,
)

export const TemplateSelectorWrapper = muiStyled('div')(
  () => `
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  padding: 0 4px;
`,
)

export const StyledTemplateSelect = muiStyled('div')(
  ({ theme }) => `
  width: 100%;

  select {
    background: ${theme.palette.background.paper};
    -webkit-appearance: auto;
    padding: 10px 12px;
    width: 100%;
    border: 1px solid ${theme.palette.divider};
    border-radius: 8px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    font-size: 0.9375rem;

    &:hover {
      border-color: ${PURPLE_THEME.light};
    }

    &:focus {
      outline: none;
      border-color: ${PURPLE_THEME.primary};
      box-shadow: 0 0 0 2px rgba(194, 178, 218, 0.25);
    }
  }
`,
)

export const ActionButtonGroup = muiStyled('div')(
  () => `
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
`,
)

export const PurpleOutlinedButton = muiStyled(Button)(
  () => `
  border-color: ${PURPLE_THEME.primary};
  color: ${PURPLE_THEME.primary};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${PURPLE_THEME.primary};
    background-color: rgba(194, 178, 218, 0.15);
    color: ${PURPLE_THEME.dark};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(116, 80, 170, 0.2);
  }

  &:disabled {
    border-color: rgba(116, 80, 170, 0.3);
    color: rgba(116, 80, 170, 0.3);
  }
`,
)

export const PurpleContainedButton = muiStyled(Button)(
  () => `
  background-color: ${PURPLE_THEME.primary};
  color: ${PURPLE_THEME.contrastText};
  transition: all 0.2s ease;
  padding: 8px 24px;
  font-weight: 600;

  &:hover {
    background-color: ${PURPLE_THEME.dark};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(116, 80, 170, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`,
)

export const SaveButtonContainer = muiStyled('div')(
  ({ theme }) => `
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 4px 16px;
  background: linear-gradient(to top, ${theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]} 85%, transparent);
  margin: 0 -16px -16px;
  z-index: 10;
  border-top: 1px solid ${theme.palette.divider};
`,
)

export const PurpleIconButton = muiStyled(IconButton)(
  () => `
  color: ${PURPLE_THEME.primary};
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(194, 178, 218, 0.3);
    color: ${PURPLE_THEME.dark};
  }
`,
)
